/**
 * NMS Countdown Daily Component
 * Rolls a <countdown-timer> over to the next day automatically at its restart time,
 * so a merchant only sets an End Date once instead of editing the date every day.
 * @extends NMSComponent
 */

// Define base NMS Component class if not already defined
window.NMSComponent =
  window.NMSComponent ||
  class {
    constructor(element) {
      this.element = element;
    }

    init() {}

    dispatchCustomEvent(eventName, detail = {}) {
      const event = new CustomEvent(`nms:${eventName}`, {
        detail: {
          component: this,
          element: this.element,
          ...detail,
          timestamp: Date.now(),
        },
        bubbles: true,
        cancelable: true,
      });

      this.element.dispatchEvent(event);
    }

    destroy() {}
  };

// Ensure NMS namespace exists
window.neemans = window.neemans || {};

const MAX_TIMEOUT = 2147483647; // setTimeout caps out at a 32-bit delay

/**
 * NMS Countdown Daily
 */
class NMSCountdownDaily extends NMSComponent {
  static componentName = 'nms-countdown-daily';

  constructor(element) {
    super(element);

    this.timeoutId = null;
    this.handleWake = this.handleWake.bind(this);

    this.resetMinutes = this.parseResetTime(element.dataset.dailyResetTime);
    this.storeOffsetMinutes = this.parseOffset(element.dataset.storeOffset);
    this.campaignEnd = this.parseDate(element.dataset.campaignEnd);

    this.init();
  }

  init() {
    // The rendered date can be stale (cached HTML, or a tab left open past midnight),
    // so always recalculate from the current time before scheduling.
    this.rollover();

    document.addEventListener('visibilitychange', this.handleWake);
    window.addEventListener('pageshow', this.handleWake);
  }

  /** "14:30" -> minutes since midnight. Falls back to midnight. */
  parseResetTime(value) {
    const match = /^(\d{1,2}):(\d{2})$/.exec((value || '').trim());
    if (!match) return 0;

    const hours = Math.min(23, parseInt(match[1], 10));
    const minutes = Math.min(59, parseInt(match[2], 10));

    return hours * 60 + minutes;
  }

  /** "+05:30" -> 330. Falls back to the visitor's own offset. */
  parseOffset(value) {
    const match = /^([+-])(\d{2}):?(\d{2})$/.exec((value || '').trim());
    if (!match) return -new Date().getTimezoneOffset();

    const total = parseInt(match[2], 10) * 60 + parseInt(match[3], 10);

    return match[1] === '-' ? -total : total;
  }

  parseDate(value) {
    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? null : date;
  }

  /** Next restart time, resolved in the store's timezone rather than the visitor's. */
  nextResetTime(from = Date.now()) {
    const offsetMs = this.storeOffsetMinutes * 60 * 1000;
    const storeNow = new Date(from + offsetMs);

    // UTC getters read the shifted date, which is the store's local calendar day
    let reset =
      Date.UTC(storeNow.getUTCFullYear(), storeNow.getUTCMonth(), storeNow.getUTCDate()) +
      this.resetMinutes * 60 * 1000 -
      offsetMs;

    if (reset <= from) {
      reset += 24 * 60 * 60 * 1000;
    }

    return reset;
  }

  /**
   * Stops one second before the restart, matching the PDP offer timer (23:59:59),
   * and never runs past the End Date - the last leg stops there instead.
   */
  nextTarget(from = Date.now()) {
    const target = this.nextResetTime(from) - 1000;

    if (this.campaignEnd && target > this.campaignEnd.getTime()) {
      return this.campaignEnd.getTime();
    }

    return target;
  }

  /**
   * <countdown-timer> only reads expires-at in connectedCallback, so the element is
   * re-inserted to make it pick up the new target.
   */
  applyTarget(target) {
    const current = new Date(this.element.getAttribute('expires-at')).getTime();

    this.element.setAttribute('expires-at', new Date(target).toISOString());

    if (Number.isNaN(current) || Math.abs(current - target) >= 1000) {
      const parent = this.element.parentNode;
      const nextSibling = this.element.nextSibling;

      if (parent) {
        parent.removeChild(this.element);
        parent.insertBefore(this.element, nextSibling);
      }
    }
  }

  rollover() {
    const now = Date.now();

    if (this.campaignEnd && now >= this.campaignEnd.getTime()) {
      this.finish();
      return;
    }

    const target = this.nextTarget(now);

    this.applyTarget(target);
    this.schedule(target);

    this.dispatchCustomEvent('countdown-daily:rollover', { target: new Date(target) });
  }

  schedule(target) {
    window.clearTimeout(this.timeoutId);

    // One extra second so the timer visibly reaches zero before restarting
    const delay = Math.min(MAX_TIMEOUT, Math.max(1000, target - Date.now() + 1000));

    this.timeoutId = window.setTimeout(() => this.rollover(), delay);
  }

  /** End Date reached - honour the section's expiry behaviour. */
  finish() {
    window.clearTimeout(this.timeoutId);

    if (this.element.getAttribute('expiration-behavior') === 'hide') {
      const section = this.element.closest('.shopify-section');
      (section || this.element).remove();
      return;
    }

    this.element.querySelectorAll('countdown-timer-flip').forEach(flip => flip.updateValue?.(0));
  }

  /** Background tabs throttle timers, so re-check whenever the page comes back. */
  handleWake() {
    if (document.visibilityState === 'hidden') return;

    this.rollover();
  }

  destroy() {
    window.clearTimeout(this.timeoutId);
    document.removeEventListener('visibilitychange', this.handleWake);
    window.removeEventListener('pageshow', this.handleWake);

    super.destroy();
  }
}

// Register component
window.neemans.NMSCountdownDaily = NMSCountdownDaily;

/**
 * Auto-initialization for daily countdown timers
 */
const initCountdownDailyComponents = () => {
  const components = document.querySelectorAll('[data-nms-js="countdown-daily"]:not([data-countdown-daily-initialized])');

  components.forEach(component => {
    new NMSCountdownDaily(component);
    component.dataset.countdownDailyInitialized = 'true';
  });
};

// Initialize on DOM ready or immediately if already loaded
document.readyState === 'loading'
  ? document.addEventListener('DOMContentLoaded', initCountdownDailyComponents)
  : initCountdownDailyComponents();

// Re-initialize on dynamic content changes (theme editor, section reloads)
document.addEventListener('shopify:section:load', initCountdownDailyComponents);
